/** Registers an eligible onboarding hint into the shared priority queue. */
// @ts-check
import { useCallback, useContext, useEffect } from 'react';
import { OnboardingHintQueueContext } from '@/features/onboarding/context/OnboardingHintQueueContext.def';

/**
 * @param {Object} params
 * @param {string} params.id
 * @param {number} params.priority
 * @param {boolean} params.eligible
 * @param {boolean} params.pending
 * @param {string} params.body
 * @param {string} [params.title]
 * @param {string} [params.testId]
 * @param {() => void} params.onDismiss
 * @param {number} [params.dismissTabIndex]
 */
export function useRegisterOnboardingQueuedHint({
  id,
  priority,
  eligible,
  pending,
  body,
  title,
  testId,
  onDismiss,
  dismissTabIndex,
}) {
  const ctx = useContext(OnboardingHintQueueContext);
  const shouldRegister =
    Boolean(ctx?.enabled) && eligible && pending;

  const stableOnDismiss = useCallback(/** @type {() => void} */ () => {
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (!ctx) {
      return;
    }
    if (!shouldRegister) {
      ctx.setRegistration(id, null);
      return () => ctx.setRegistration(id, null);
    }
    ctx.setRegistration(id, {
      priority,
      eligible,
      body,
      title,
      testId,
      onDismiss: /** @type {() => void} */ (stableOnDismiss),
      dismissTabIndex,
    });
    return () => ctx.setRegistration(id, null);
  }, [
    ctx,
    id,
    shouldRegister,
    priority,
    eligible,
    body,
    title,
    testId,
    stableOnDismiss,
    dismissTabIndex,
  ]);

  return {
    isActive: ctx?.activeId === id,
  };
}
